<template>
  <div id="order-details-draft" class="fill-height align-start">
    <v-dialog
      v-model="confirmationDialog"
      content-class="i-dialog"
      width="400"
      persistent
      @click:outside="confirmationDialog = false"
      @keydown.esc="confirmationDialog = false"
    >
      <v-card>
        <template>
          <v-card-title class="i-dialog-header-div">
            <span class="g-heading-m g-bold">Despatch Information</span>
          </v-card-title>

          <v-card-text class="py-4 text-center">
            <template
              v-if="
                mutableOrder.paymentType === 'SUPPLIER_DIRECT' &&
                mutableOrder.offlinePaymentDays === 0
              "
            >
              <span class="font-weight-bold"
                >Prepayment, finalise data & confirm,
                {{ isILR ? 'Customer' : 'Retailer' }} is sent the Invoice, goods
                to be paid for prior to dispatch</span
              ><br /><br />
            </template>

            <span>Once confirmed, order will move to</span><br />
            <span><strong>In Transit</strong> for Despatch Information.</span
            ><br />
            <span>Do you want to confirm now?</span>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="i-dialog-actions-div justify-center">
            <v-btn
              rounded
              text
              class="i-btn-text-black"
              @click="confirmationDialog = false"
            >
              Cancel
            </v-btn>

            <v-btn
              rounded
              class="i-btn-block-secondary"
              @click="submit"
              :loading="submitting"
            >
              Confirm
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="cancelConfirmationDialog"
      content-class="i-dialog"
      width="400"
      persistent
      @click:outside="cancelConfirmationDialog = false"
      @keydown.esc="cancelConfirmationDialog = false"
    >
      <v-card>
        <template>
          <v-card-title class="i-dialog-header-div">
            <span class="g-heading-m g-bold">Cancel Order</span>
          </v-card-title>

          <v-card-text class="py-4 text-center">
            <div v-if="totalNumberOfCartons === 0" class="mb-3">
              You have zeroed out all quantities which is the equivalent of
              cancelling an order.
            </div>
            <span><strong>Specify reason for cancellation</strong></span>
            <br />
            <v-text-field
              append-icon="mdi-message-reply-text"
              class="pa-0 mt-2 mb-n1"
              counter="255"
              maxlength="255"
              dense
              label="Reason for cancellation"
              outlined
              required
              v-model="reasonForCancellation"
            ></v-text-field>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="i-dialog-actions-div justify-center">
            <v-btn
              rounded
              text
              class="i-btn-text-black"
              @click="cancelConfirmationDialog = false"
            >
              Close
            </v-btn>

            <v-btn
              rounded
              class="i-btn-block-secondary"
              @click="cancelOrder"
              :disabled="!reasonForCancellation"
              :loading="cancelling"
            >
              Confirm
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>

    <v-row class="g-container" no-gutters>
      <v-col>
        <i-breadcrumbs :breadcrumbs="breadcrumbs"></i-breadcrumbs>
        <h2 class="ma-0 mt-2">{{ $route.meta.title }}</h2>

        <template v-if="$vuetifyDisplay.smAndDown">
          <v-col class="mt-2 pa-0">
            <v-card class="rounded-2 mt-4 mb-6">
              <v-row no-gutters align="center" class="p-div-title py-3 px-3">
                <v-col cols="12">
                  <p class="g-heading-m g-bold p-title mr-2">
                    Purchase Order Number
                  </p>
                  <p class="g-heading-m g-bold p-title mr-2">
                    {{ `#${mutableOrder.supplierOrderNumber}` }}
                  </p>
                </v-col>
              </v-row>

              <div class="pa-3">
                <v-row no-gutters>
                  <v-col cols="6" class="py-1">
                    <span>ORDER DATE & TIME</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{
                      $filters.formatDateTime12h(mutableOrder.createdAt)
                    }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>PO TOTAL{{ incTaxSuffixUpper }}</span>
                  </v-col>
                  <v-col v-if="mutableOrder.summary" cols="6" class="py-1 pl-4">
                    <span>{{
                      $filters.currency(displayOrderTotal(mutableOrder.summary))
                    }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span class="p-response-date"
                      >REQUIRED RESPONSE DATE & TIME</span
                    >
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span class="p-response-date">{{
                      $filters.formatDateTime12h(
                        mutableOrder.expectedFinalConfirmationDate
                      )
                    }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>PAYMENT TYPE</span>
                  </v-col>
                  <v-col
                    cols="6"
                    class="py-1 pl-4"
                    :class="{
                      'font-weight-bold':
                        mutableOrder.paymentType === 'SUPPLIER_DIRECT'
                    }"
                  >
                    <i-payment-type
                      v-if="mutableOrder.paymentType"
                      :paymentType="mutableOrder.paymentType"
                      :textOnly="true"
                    />
                    <span
                      v-if="mutableOrder.paymentType === 'SUPPLIER_DIRECT'"
                      class="error--text text--darken-1"
                    >
                      <template v-if="mutableOrder.offlinePaymentDays">
                        ({{ mutableOrder.offlinePaymentDays }} days)
                      </template>
                      <template v-else> (Prepayment) </template>
                    </span>
                  </v-col>
                </v-row>

                <v-divider class="mt-3 mb-2"></v-divider>

                <v-row no-gutters>
                  <template v-if="hasBannerGroup">
                    <v-col cols="6" class="py-1">
                      <span>BANNER GROUP</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{ mutableOrder.store.bannerGroupName }}</span>
                    </v-col>
                  </template>
                  <v-col cols="6" class="py-1">
                    <span>{{ isILR ? 'CUSTOMER' : 'RETAILER' }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{ showStoreName }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>DELIVERY ADDRESS</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{ showDeliveryAddress }}</span>
                  </v-col>
                </v-row>

                <v-divider class="mt-3 mb-2"></v-divider>

                <div class="py-2">
                  <strong>{{ isILR ? 'CUSTOMER' : 'RETAILER' }} MESSAGE</strong>
                  <div class="i-message">
                    <span>{{ retailerMessage }}</span>
                  </div>
                </div>

                <div class="py-2" v-if="supplierMessages">
                  <strong>SUPPLIER MESSAGE(S)</strong>
                  <div class="i-message">
                    <span
                      v-for="(supplierMessage, index) in supplierMessages"
                      :key="index"
                      >{{ supplierMessage }}<br
                    /></span>
                  </div>
                </div>

                <v-divider class="mt-3"></v-divider>

                <div class="text-center pt-2">
                  <v-btn
                    class="i-btn-block-primary g-full-width"
                    @click="fetchPDF()"
                    :disabled="!mutableOrder.draftPdfAvailable"
                    :loading="fetchingPDF"
                  >
                    <span>Export PDF</span>
                    <v-icon color="white" class="ml-1">mdi-file-pdf-box</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card>

            <v-card
              v-for="item in mutableOrder.supplierOrderItems"
              :key="item.id"
              class="rounded-2 pa-4 pa-md-6 mb-6"
            >
              <v-row no-gutters>
                <v-col cols="6" class="py-1">
                  <span>{{ headers[0].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{ item.supplierProductCode }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span>{{ headers[1].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{ item.productDescription }}</span>
                  <v-chip
                    v-if="!item.isFreeProduct"
                    x-small
                    label
                    class="free-tag"
                  >
                    FREE
                  </v-chip>
                </v-col>
                <v-col v-if="!isServiceOrder" cols="6" class="py-1">
                  <span>{{ headers[2].text }}</span>
                </v-col>
                <v-col v-if="!isServiceOrder" cols="6" class="py-1 pl-4">
                  <span>{{ item.gtin }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span>
                    {{ isServiceOrder ? headers[2].text : headers[3].text }}
                  </span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span>{{ showItemPrice(item) }}</span>
                </v-col>
                <template v-if="isServiceOrder">
                  <v-col cols="6" class="py-1">
                    <span>{{ headers[3].text }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{ showItemQuantity(item.originalQuantity) }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>{{ headers[4].text }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <div>
                      <v-chip x-small label class="i-tag-primary"
                        >Step 1</v-chip
                      >
                    </div>
                  </v-col>
                  <v-col class="pb-0">
                    <i-stepper
                      :value="item.revisedQuantity"
                      :max="item.originalQuantity"
                      prevent-empty-quantity
                      :isFreeProduct="item.isFreeProduct"
                      @change="
                        (availableQty) =>
                          handleQuantityChanged(item, availableQty)
                      "
                    />
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>{{ headers[5].text }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{
                      $filters.formatDate(item.retailerRequestedDate)
                    }}</span>
                  </v-col>
                </template>
              </v-row>

              <v-divider v-if="!isServiceOrder" class="my-2"></v-divider>

              <v-row v-if="!isServiceOrder">
                <v-col cols="6" class="py-1">
                  <span class="font-weight-bold">{{ headers[4].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <span class="font-weight-bold">{{
                    showItemQuantity(item.originalQuantity)
                  }}</span>
                </v-col>
                <v-col cols="6" class="py-1">
                  <span class="font-weight-bold">{{ headers[5].text }}</span>
                </v-col>
                <v-col cols="6" class="py-1 pl-4">
                  <div>
                    <v-chip x-small label class="i-tag-primary">Step 1</v-chip>
                  </div>
                </v-col>
                <v-col class="pb-0">
                  <i-stepper
                    :value="item.revisedQuantity"
                    :max="item.originalQuantity"
                    prevent-empty-quantity
                    :isFreeProduct="item.isFreeProduct"
                    @change="
                      (availableQty) =>
                        handleQuantityChanged(item, availableQty)
                    "
                  />
                </v-col>
              </v-row>

              <template
                v-if="!isServiceOrder && item.weightType === 'VARIABLE_WEIGHT'"
              >
                <v-divider class="my-2"></v-divider>

                <v-row no-gutters class="mt-2">
                  <v-col cols="6" class="py-1">
                    <span class="font-weight-bold">{{ headers[6].text }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span class="font-weight-bold">{{
                      `${item.originalVariableWeightInKg} KG`
                    }}</span>
                  </v-col>

                  <v-col cols="6" class="py-1">
                    <span>{{ headers[8].text }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{ `${item.maximumVariableWeightInKg} KG` }}</span>
                  </v-col>

                  <v-col cols="6" class="py-1">
                    <span class="font-weight-bold">{{ headers[7].text }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <div>
                      <v-chip x-small label class="i-tag-primary"
                        >Step 2</v-chip
                      >
                    </div>
                  </v-col>
                  <v-col>
                    <i-numeric-field
                      background-color="#F0F0F0"
                      class="mt-2"
                      dense
                      hide-details="auto"
                      label="Delivered KG"
                      :min="0"
                      :max="9999999.99"
                      :precision="NUM_DECIMALS_FOR_VW_KG"
                      outlined
                      :rules="[
                        (v) => !!v || 'Delivered KG required',
                        (v) =>
                          +maxNumberDecimals(v, NUM_DECIMALS_FOR_VW_KG) <=
                            +item.maximumVariableWeightInKg ||
                          'Over Max. allowed KG'
                      ]"
                      suffix="KG"
                      :value="item.availableWeightInKg"
                      @input="
                        (availableKg) =>
                          (item.availableWeightInKg = availableKg)
                      "
                      @change="
                        (availableKg) =>
                          (item.availableWeightInKg = maxNumberDecimals(
                            availableKg,
                            NUM_DECIMALS_FOR_VW_KG
                          ))
                      "
                    ></i-numeric-field>
                  </v-col>
                </v-row>
              </template>

              <br />
              <div class="text-center">Update quantity if required</div>
            </v-card>

            <v-btn
              rounded
              text
              class="my-2 i-btn-text-red g-full-width"
              @click="cancelConfirmationDialog = true"
            >
              Cancel Order
            </v-btn>
            <v-btn
              rounded
              class="i-btn-block-orange g-full-width"
              :disabled="invalidOrder"
              @click="confirmOrderClicked()"
            >
              Confirm
            </v-btn>
          </v-col>
        </template>

        <template v-else>
          <v-card class="rounded-2 mt-4">
            <v-row no-gutters align="center" class="p-div-title pt-3 pb-3 px-4">
              <v-col cols="12" md="6">
                <span class="g-heading-m g-bold p-title mr-2">
                  Purchase Order Number
                  {{ `#${mutableOrder.supplierOrderNumber}` }}
                </span>
              </v-col>

              <v-col cols="12" md="6" class="text-right">
                <v-btn
                  class="i-btn-block-primary"
                  @click="fetchPDF()"
                  :disabled="!mutableOrder.draftPdfAvailable"
                  :loading="fetchingPDF"
                >
                  <span>Export PDF</span>
                  <v-icon color="white" class="ml-1">mdi-file-pdf-box</v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <v-row class="px-4">
              <v-col class="mb-3" cols="12" md="6" lg="5">
                <v-simple-table>
                  <tr>
                    <td>Order Date & Time</td>
                    <td>
                      {{ $filters.formatDateTime12h(mutableOrder.createdAt) }}
                    </td>
                  </tr>
                  <tr v-if="mutableOrder.summary">
                    <td>PO Total{{ incTaxSuffix }}</td>
                    <td>
                      {{
                        $filters.currency(
                          displayOrderTotal(mutableOrder.summary)
                        )
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong class="p-response-date"
                        >Required Response Date & Time</strong
                      >
                    </td>
                    <td>
                      {{
                        $filters.formatDateTime12h(
                          mutableOrder.expectedFinalConfirmationDate
                        )
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td>Payment Type:</td>
                    <td
                      :class="{
                        'font-weight-bold':
                          mutableOrder.paymentType === 'SUPPLIER_DIRECT'
                      }"
                    >
                      <i-payment-type
                        v-if="mutableOrder.paymentType"
                        :paymentType="mutableOrder.paymentType"
                        :textOnly="true"
                      />
                      <span
                        v-if="mutableOrder.paymentType === 'SUPPLIER_DIRECT'"
                        class="error--text text--darken-1"
                      >
                        <template v-if="mutableOrder.offlinePaymentDays">
                          ({{ mutableOrder.offlinePaymentDays }} days)
                        </template>
                        <template v-else> (Prepayment) </template>
                      </span>
                    </td>
                  </tr>
                </v-simple-table>
              </v-col>

              <v-spacer></v-spacer>

              <v-col class="mb-3" cols="12" md="6" lg="5">
                <v-simple-table>
                  <tr v-if="hasBannerGroup">
                    <td>Banner Group</td>
                    <td>{{ mutableOrder.store.bannerGroupName }}</td>
                  </tr>
                  <tr>
                    <td>{{ isILR ? 'Customer' : 'Retailer' }}</td>
                    <td>{{ showStoreName }}</td>
                  </tr>
                  <tr>
                    <td>Delivery Address</td>
                    <td>{{ showDeliveryAddress }}</td>
                  </tr>
                </v-simple-table>
              </v-col>

              <v-col class="pt-2 pb-4" cols="12">
                <div>
                  <strong>{{ isILR ? 'CUSTOMER' : 'RETAILER' }} MESSAGE</strong>
                </div>
                <div class="i-message">
                  <span>{{ retailerMessage }}</span>
                </div>
              </v-col>

              <v-col class="pt-2 pb-4" cols="12" v-if="supplierMessages">
                <div><strong>SUPPLIER MESSAGE(S)</strong></div>
                <div class="i-message">
                  <span
                    v-for="(supplierMessage, index) in supplierMessages"
                    :key="index"
                    >{{ supplierMessage }}<br
                  /></span>
                </div>
              </v-col>
            </v-row>

            <v-divider class="my-2"></v-divider>

            <v-data-table
              :headers="headers"
              :items="mutableOrder.supplierOrderItems"
              :items-per-page="-1"
              disable-sort
              hide-default-footer
            >
              <template v-slot:[`header.revisedQuantity`]="{ header }">
                <div class="mt-n2 mb-n1">
                  <v-chip x-small label class="i-tag-primary">Step 1</v-chip>
                </div>
                <div>
                  <span>{{ header.text }}</span>
                </div>
              </template>

              <template #[`header.maximumVariableWeightInKg`]="{ header }">
                {{ header.text }}
                <v-icon
                  class="i-tooltip-color pb-1"
                  small
                  v-tooltip="'Applies to Variable Weight products only'"
                >
                  mdi-help-circle-outline
                </v-icon>
              </template>

              <template #[`header.availableWeightInKg`]="{ header }">
                <div v-if="orderHasVariableWeightProduct" class="mt-n2 mb-n1">
                  <v-chip x-small label class="i-tag-primary">Step 2</v-chip>
                </div>
                <div>
                  <span>
                    {{ header.text }}
                    <v-icon
                      class="i-tooltip-color pb-1"
                      small
                      v-tooltip="'Applies to Variable Weight products only'"
                    >
                      mdi-help-circle-outline
                    </v-icon>
                  </span>
                </div>
              </template>

              <template v-slot:[`item.productDescription`]="{ item }">
                <span>{{ item.productDescription }}</span>
                <v-chip
                  v-if="item.isFreeProduct"
                  x-small
                  label
                  class="free-tag"
                >
                  FREE
                </v-chip>
              </template>

              <template v-slot:[`item.totalPrice`]="{ item }">
                <span>{{ showItemPrice(item) }}</span>
              </template>

              <template v-slot:[`item.retailerRequestedDate`]="{ item }">
                <span>{{
                  $filters.formatDate(item.retailerRequestedDate)
                }}</span>
              </template>

              <template v-slot:[`item.revisedQuantity`]="{ item }">
                <i-stepper
                  :value="item.revisedQuantity"
                  :max="item.originalQuantity"
                  prevent-empty-quantity
                  :isFreeProduct="item.isFreeProduct"
                  @change="
                    (availableQty) => handleQuantityChanged(item, availableQty)
                  "
                />
              </template>

              <template v-slot:[`item.originalVariableWeightInKg`]="{ item }">
                <span v-if="item.originalVariableWeightInKg">{{
                  `${item.originalVariableWeightInKg} KG`
                }}</span>
                <span v-else>-</span>
              </template>

              <template v-slot:[`item.maximumVariableWeightInKg`]="{ item }">
                <span v-if="item.maximumVariableWeightInKg">{{
                  `${item.maximumVariableWeightInKg} KG`
                }}</span>
                <span v-else>-</span>
              </template>

              <template v-slot:[`item.availableWeightInKg`]="{ item }">
                <div>
                  <i-numeric-field
                    v-if="item.weightType === 'VARIABLE_WEIGHT'"
                    background-color="#F0F0F0"
                    class="mt-3 mb-n4"
                    dense
                    :min="0"
                    :max="9999999.99"
                    :precision="NUM_DECIMALS_FOR_VW_KG"
                    outlined
                    :rules="[
                      (v) => !!v || 'Delivered KG required',
                      (v) =>
                        +maxNumberDecimals(v, NUM_DECIMALS_FOR_VW_KG) <=
                          +item.maximumVariableWeightInKg ||
                        'Over Max. allowed KG'
                    ]"
                    suffix="KG"
                    :value="item.availableWeightInKg"
                    @input="
                      (availableKg) => (item.availableWeightInKg = availableKg)
                    "
                    @change="
                      (availableKg) =>
                        (item.availableWeightInKg = maxNumberDecimals(
                          availableKg,
                          NUM_DECIMALS_FOR_VW_KG
                        ))
                    "
                  ></i-numeric-field>
                  <span v-else>-</span>
                </div>
              </template>
            </v-data-table>

            <v-divider class="my-2"></v-divider>

            <v-row justify="end">
              <v-col cols="8" class="py-0 text-left">
                <v-btn
                  rounded
                  text
                  class="mr-4 i-btn-text-red"
                  @click="cancelConfirmationDialog = true"
                >
                  Cancel Order
                </v-btn>
              </v-col>
              <v-col cols="4" class="py-0 text-right">
                <v-btn
                  rounded
                  class="i-btn-block-orange p-confirm-btn mt-2 mr-4 mb-4"
                  :disabled="invalidOrder"
                  @click="confirmOrderClicked()"
                >
                  Confirm
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters, mapState } from 'vuex'
import {
  fetchOrderPDF,
  submitOrder,
  cancelOrder
} from '@/services/supplierOrders'
import { openFile } from '@/utils/common'
import iBreadcrumbs from '@/components/Breadcrumbs'
import iNumericField from '@/components/NumericField'
import iStepper from '@/components/Stepper'
import iPaymentType from '@/components/PaymentType'
import { fetchBuyGetFreePromotions } from '@/services/buyGetPromotion'
import {
  pickSupplierAmount,
  pickSupplierCostTotal
} from '@/utils/supplierAmountDisplay'

const NUM_DECIMALS_FOR_VW_KG = 2
const VARIATION_FOR_VG_KG_IN_PERCENT = 15

export default {
  props: ['orderDetails'],
  components: {
    iPaymentType,
    iBreadcrumbs,
    iNumericField,
    iStepper
  },

  data() {
    return {
      breadcrumbs: [
        { text: 'Home', exact: true, to: { name: 'dashboard' } },
        {
          text: 'Purchase Orders',
          exact: true,
          to: { name: 'purchase-orders' }
        },
        { text: this.$route.meta.title, to: null }
      ],
      confirmationDialog: false,
      fetchingPDF: false,
      cancelConfirmationDialog: false,
      quantitiesMap: {},
      mutableOrder: {},
      submitting: false,
      cancelling: false,
      reasonForCancellation: null,
      NUM_DECIMALS_FOR_VW_KG,
      VARIATION_FOR_VG_KG_IN_PERCENT
    }
  },

  mounted() {
    this.mutableOrder = this.mapOrder(this.orderDetails)
    this.loadBuyGetFreePromotions()
  },

  computed: {
    ...mapGetters('tenant', ['isILR', 'tenantCountryRegion']),
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapState('familyGroups', ['familyGroups']),
    headers() {
      const headers = [
        {
          text: 'SUPPLIER ITEM CODE',
          value: 'supplierProductCode',
          align: 'start'
        },
        {
          text: 'PRODUCT DESCRIPTION',
          value: 'productDescription',
          align: 'start'
        },
        { text: 'CARTON GTIN', value: 'gtin', align: 'left' },
        {
          text: this.tenantCountryRegion === 'US' ? 'TOTAL' : 'TOTAL (INC TAX)',
          value: 'totalPrice',
          align: 'center'
        },
        { text: 'ORDERED QTY', value: 'originalQuantity', align: 'center' },
        { text: 'DELIVERED QTY', value: 'revisedQuantity', align: 'center' },
        {
          text: 'ORDERED KG',
          value: 'originalVariableWeightInKg',
          align: 'center'
        },
        {
          text: 'DELIVERED KG',
          value: 'availableWeightInKg',
          align: 'center',
          width: '160'
        },
        {
          text: 'MAX. ALLOWED KG',
          value: 'maximumVariableWeightInKg',
          align: 'center'
        }
      ]

      if (!this.isServiceOrder) return headers.filter((i) => !i.hidden)

      return [
        headers[0],
        headers[1],
        {
          text: this.tenantCountryRegion === 'US' ? 'TOTAL' : 'TOTAL (INC TAX)',
          value: 'totalPrice',
          align: 'center'
        },
        { text: 'ORDERED QTY', value: 'originalQuantity', align: 'center' },
        { text: 'DELIVERED QTY', value: 'revisedQuantity', align: 'center' },
        {
          text: 'REQUESTED DATE',
          value: 'retailerRequestedDate',
          align: 'center'
        }
      ]
    },

    hasBannerGroup() {
      return !!this.mutableOrder.store?.bannerGroupName
    },

    invalidOrder() {
      return !!this.mutableOrder.supplierOrderItems?.find(
        (item) =>
          item.weightType === 'VARIABLE_WEIGHT' &&
          (item.availableWeightInKg === null ||
            +item.availableWeightInKg > +item.maximumVariableWeightInKg ||
            (item.revisedQuantity > 0 && !(item.availableWeightInKg > 0)))
      )
    },

    totalNumberOfCartons() {
      return this.mutableOrder?.supplierOrderItems?.reduce(
        (aggregatedNumberOfCartons, supplierOrderItem) =>
          aggregatedNumberOfCartons + supplierOrderItem.revisedQuantity,
        0
      )
    },

    orderHasVariableWeightProduct() {
      return !!this.mutableOrder?.supplierOrderItems?.find(
        (product) => product.weightType === 'VARIABLE_WEIGHT'
      )
    },

    isServiceOrder() {
      const items = this.mutableOrder?.supplierOrderItems
      if (!items || !items.length) return false

      return items.every(
        (item) => item.productType === 'SERVICE' || !!item.retailerRequestedDate
      )
    },

    retailerMessage() {
      return this.orderDetails?.orderNotes[0]?.message || 'None'
    },

    supplierMessages() {
      if (!this.orderDetails?.supplierOrderNotes?.length) return
      return this.orderDetails?.supplierOrderNotes.map((m) => m.message)
    },

    showStoreName() {
      return this.mutableOrder.store?.name !== undefined
        ? this.mutableOrder.store?.name
        : 'Not available'
    },

    showDeliveryAddress() {
      return this.mutableOrder.deliveryAddress !== undefined
        ? this.mutableOrder.deliveryAddress.line1 +
            ', ' +
            this.mutableOrder.deliveryAddress.suburb +
            ', ' +
            this.mutableOrder.deliveryAddress.state +
            ' ' +
            this.mutableOrder.deliveryAddress.postCode
        : 'Not available'
    },

    tableHeight() {
      const DEFAULT_ROW_HEIGHT = 48
      const MAX_ROWS_VISIBLE = 5
      return this.mutableOrder.supplierOrderItems?.length > MAX_ROWS_VISIBLE
        ? DEFAULT_ROW_HEIGHT * (MAX_ROWS_VISIBLE + 1) // +1 as header is also included
        : undefined
    },

    incTaxSuffix() {
      return this.tenantCountryRegion === 'US' ? '' : ' (inc Tax)'
    },

    incTaxSuffixUpper() {
      return this.tenantCountryRegion === 'US' ? '' : ' (INC TAX)'
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    mapOrder(order) {
      const clonedOrder = _.cloneDeep(order)

      clonedOrder.supplierOrderItems = clonedOrder.supplierOrderItems
        .map((item) => {
          if (item.originalVariableWeightInKg) {
            item.availableWeightInKg = null
            item.variableWeightInKg = this.maxNumberDecimals(
              item.originalVariableWeightInKg,
              NUM_DECIMALS_FOR_VW_KG
            )
            item.maximumVariableWeightInKg = this.maxNumberDecimals(
              this.upperAllowedAvailableKg(
                item.originalVariableWeightInKg,
                VARIATION_FOR_VG_KG_IN_PERCENT
              ),
              NUM_DECIMALS_FOR_VW_KG
            )
          }

          return {
            ...item,
            isFreeProduct:
              [89, 813].includes(this.selectedSupplierId) &&
              item.summary?.totalIncTax === 0
                ? false
                : item.isFreeProduct,
            revisedQuantity: item.originalQuantity,
            weightType:
              item.originalVariableWeightInKg >= 0
                ? 'VARIABLE_WEIGHT'
                : 'FIXED_WEIGHT'
          }
        })
        .sort((a, b) => {
          if (a.originalVariableWeightInKg && !b.originalVariableWeightInKg) {
            return -1
          } else if (
            !a.originalVariableWeightInKg &&
            b.originalVariableWeightInKg
          ) {
            return 1
          } else return a.productDescription > b.productDescription ? 1 : -1
        })

      return clonedOrder
    },

    maxNumberDecimals(value, numberDecimals) {
      if (value < 0) return 0
      return Number(value).toFixed(numberDecimals)
    },

    upperAllowedAvailableKg(weigthInKg, variationPercent) {
      return +weigthInKg + +weigthInKg * (variationPercent / 100)
    },

    fetchPDF() {
      this.fetchingPDF = true
      fetchOrderPDF(this.mutableOrder.id)
        .then((response) => {
          const file = new Blob([response], { type: 'application/pdf' })
          const fileURL = URL.createObjectURL(file)
          openFile(fileURL)
        })
        .catch((err) => {
          err.response.status === 404
            ? this.addNotification({
                color: 'error',
                text: `Invoice ${this.mutableOrder.id} not found`
              })
            : this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.fetchingPDF = false
        })
    },

    showItemPrice(item) {
      return this.$options.filters.currency(
        pickSupplierAmount({
          tenantCountryRegion: this.tenantCountryRegion,
          exTax: item.summary?.totalExTax,
          incTax: item.summary?.totalIncTax
        })
      )
    },

    displayOrderTotal(summary) {
      return pickSupplierCostTotal({
        tenantCountryRegion: this.tenantCountryRegion,
        cost: summary?.remittanceTotal
      })
    },

    showItemQuantity(quantity) {
      return quantity !== undefined ? quantity : 'Not available'
    },

    handleQuantityChanged(item, availableQty) {
      item.revisedQuantity = availableQty

      const promos = this.mutableOrder?.promotions || []
      const promotion = promos.find(
        (promo) =>
          promo.productCode === item.productCode ||
          (promo.familyGroup &&
            promo.familyGroup.id &&
            this.isProductInFamilyGroup(
              promo.familyGroup.id,
              item.productCode
            )) ||
          (promo.byRange &&
            (promo.rangeProducts || []).includes(item.productCode))
      )

      if (promotion) {
        let totalBuyQty = 0

        if (promotion.familyGroup && promotion.familyGroup.id) {
          totalBuyQty = (this.mutableOrder.supplierOrderItems || [])
            .filter(
              (p) =>
                this.isProductInFamilyGroup(
                  promotion.familyGroup.id,
                  p.productCode
                ) && !p.isFreeProduct
            )
            .reduce((sum, p) => sum + (p.revisedQuantity || 0), 0)
        } else if (promotion.byRange) {
          const range = promotion.rangeProducts || []
          totalBuyQty = (this.mutableOrder.supplierOrderItems || [])
            .filter((p) => range.includes(p.productCode) && !p.isFreeProduct)
            .reduce((sum, p) => sum + (p.revisedQuantity || 0), 0)
        } else {
          totalBuyQty = availableQty || 0
        }

        const tiers = [...(promotion.tiers || [])].sort(
          (a, b) => a.buyQty - b.buyQty
        )
        if (tiers.length > 0) {
          let bestTier = tiers[0]
          for (const t of tiers) {
            if (totalBuyQty >= t.buyQty) bestTier = t
            else break
          }

          let freeQuantity = 0
          if (totalBuyQty >= tiers[0].buyQty) {
            freeQuantity =
              Math.floor(totalBuyQty / bestTier.buyQty) * bestTier.freeQty
          }

          const cap = Number(promotion.maxFreeUnitsCap)
          if (!Number.isNaN(cap) && cap > 0) {
            freeQuantity = Math.min(freeQuantity, cap)
          }

          const freeProductCode = promotion.freeProductCode
          if (freeProductCode) {
            const existingFree = (
              this.mutableOrder.supplierOrderItems || []
            ).find((p) => p.productCode === freeProductCode && p.isFreeProduct)
            if (existingFree) {
              existingFree.revisedQuantity = freeQuantity
            }
          }
        }
      }

      if (item.weightType === 'VARIABLE_WEIGHT') {
        if (item.revisedQuantity === 0) {
          item.availableWeightInKg = item.maximumVariableWeightInKg =
            this.maxNumberDecimals(0, NUM_DECIMALS_FOR_VW_KG)
        } else {
          item.maximumVariableWeightInKg = this.maxNumberDecimals(
            this.upperAllowedAvailableKg(
              item.originalVariableWeightInKg,
              VARIATION_FOR_VG_KG_IN_PERCENT
            ),
            NUM_DECIMALS_FOR_VW_KG
          )
        }
      }
    },
    isProductInFamilyGroup(familyGroupId, productCode) {
      const familyGroup = this.familyGroups.find(
        (fg) => fg.id === familyGroupId
      )
      return familyGroup.productCodes.includes(productCode)
    },

    confirmOrderClicked() {
      if (this.totalNumberOfCartons === 0) {
        this.cancelConfirmationDialog = true
      } else {
        this.confirmationDialog = true
      }
    },

    cancelOrder() {
      this.cancelling = true

      const request = {
        reasonForCancellation: this.reasonForCancellation
      }

      cancelOrder(this.mutableOrder.id, request)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Order successfully cancelled!'
          })
          this.$router.push({ name: 'purchase-orders' })
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Order could not be cancelled'
          })
        })
        .finally(() => {
          this.cancelling = false
          this.cancelConfirmationDialog = false
        })
    },

    submit() {
      this.submitting = true

      const request = {
        items: this.mutableOrder.supplierOrderItems.map((item) => {
          return {
            id: item.id,
            productCode: item.productCode,
            quantity: item.revisedQuantity,
            variableWeightInKg: item.availableWeightInKg
          }
        })
      }

      submitOrder(this.mutableOrder.id, request)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Order successfully submitted!',
            arrText:
              'Submitted Draft Purchase Orders are available in the In Transit tab'
          })
          this.$router.push({ name: 'purchase-orders' })
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Order could not be submitted, please try again...'
          })
        })
        .finally(() => {
          this.submitting = false
        })
    },

    async loadBuyGetFreePromotions() {
      try {
        const { data } = await fetchBuyGetFreePromotions(
          this.selectedSupplierId
        )
        const storeState = this.mutableOrder.deliveryAddress.state
        this.mutableOrder.promotions = data
          .filter((promo) => {
            const isActive =
              promo.status === 'ACTIVE' &&
              (!promo.endDate ||
                promo.endDate >= new Date().toISOString().split('T')[0])
            const isNational =
              !promo.targetStates || promo.targetStates.length === 0
            const isStateMatch =
              storeState &&
              promo.targetStates.some(
                (state) =>
                  state.state.toUpperCase() === storeState.toUpperCase()
              )
            return isActive && (isNational || isStateMatch)
          })
          .map((promo) => {
            if (promo.targetStates.length > 0) {
              promo.tiers = promo.tiers.filter((tier) =>
                tier.targetStates.some((state) => state.state === storeState)
              )
            }
            return promo.tiers.length > 0 ? promo : null
          })
          .filter((promo) => promo !== null)
      } catch (error) {
        console.error('Failed to fetch Buy Get Free promotions:', error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#order-details-draft {
  table {
    border-collapse: collapse;
    tr > td {
      padding: 2px 0px;
    }
    tr > td:nth-of-type(1) {
      font-weight: bold;
    }
  }
  .p-div-title {
    background-color: $primary-color-light;
    color: $primary-color;
  }
  .p-title {
    display: inline-block;
    letter-spacing: 0.75px;
  }
  .p-response-date {
    color: $secondary-color;
  }
  .i-message {
    background-color: transparent;
    margin-top: 4px;
    padding: 10px;
  }
  .free-tag {
    background-color: $secondary-color;
    color: white;
    font-weight: bold;
    margin-left: 5px;
    padding: 2px 6px;
    border-radius: 5px;
    font-size: 12px;
  }
}

@media only screen and (min-width: 960px) {
  #order-details-draft {
    .p-confirm-btn {
      width: 200px;
    }
  }
}
</style>
